/**
 * Shared single-file document upload modal (vendor portal + contractor-for-sub).
 * Full-dialog drop target, file chip + progress, soft duplicate warn.
 */
import { useState, useRef, useCallback } from 'react'
import { Upload } from 'lucide-react'
import UploadFileChip from './UploadFileChip'
import {
  humanUploadError,
  nextChipPhase,
  isDuplicateFile,
  duplicateWarnMessage,
  type ChipPhase,
} from './uploadChipModel'

export const HUB_DOC_TYPES = [
  { value: 'license',     label: 'License' },
  { value: 'insurance',   label: 'Insurance Certificate' },
  { value: 'w9',          label: 'W-9 Form' },
  { value: 'invoice',     label: 'Invoice' },
  { value: 'lien_waiver', label: 'Lien Waiver' },
  { value: 'other',       label: 'Other' },
]

const MAX_BYTES = 25 * 1024 * 1024
const ACCEPT = '.pdf,.docx,.doc,.xlsx,.jpg,.jpeg,.png'

export interface DocUploadModalProps {
  defaultType: string
  onClose: () => void
  onSuccess: () => void
  /** Returns a promise; optional onProgress 0–100 for chip bar */
  upload: (
    file: File,
    docType: string,
    opts: { insuranceExpires?: string | null },
    onProgress?: (pct: number) => void,
  ) => Promise<void>
  title?: string
  showInsuranceExpiry?: boolean
  /** Existing docs for client duplicate warn (name+size) */
  existingFiles?: Array<{ original_name?: string; file_size?: number }>
  /** Optional async server duplicate check */
  checkDuplicate?: (file: File) => Promise<boolean>
}

function hasFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types || []).includes('Files')
}

export default function DocUploadModal({
  defaultType,
  onClose,
  onSuccess,
  upload,
  title = 'Upload document',
  showInsuranceExpiry = false,
  existingFiles = [],
  checkDuplicate,
}: DocUploadModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState(defaultType)
  const [expiresOn, setExpiresOn] = useState('')
  const [dragging, setDragging] = useState(false)
  const [phase, setPhase] = useState<ChipPhase>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [dupPrompt, setDupPrompt] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)

  const pickFile = useCallback(async (f: File) => {
    setError(null)
    setDupPrompt(null)
    if (f.size > MAX_BYTES) {
      setFile(f)
      setPhase('error')
      setError(humanUploadError('File too large'))
      return
    }
    const clientDup = isDuplicateFile(f, existingFiles)
    let serverDup = false
    if (!clientDup && checkDuplicate) {
      try {
        serverDup = await checkDuplicate(f)
      } catch { /* non-fatal — allow upload */ }
    }
    if (clientDup || serverDup) {
      setFile(f)
      setPhase('ready')
      setDupPrompt(duplicateWarnMessage(f.name))
      return
    }
    setFile(f)
    setPhase(nextChipPhase('idle', 'pick'))
  }, [existingFiles, checkDuplicate])

  const onDragEnter = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current += 1
    setDragging(true)
  }
  const onDragOver = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    e.stopPropagation()
  }
  const onDragLeave = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current = 0
    setDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f) void pickFile(f)
  }

  async function runUpload() {
    if (!file || phase === 'uploading') return
    setDupPrompt(null)
    setPhase('uploading')
    setProgress(8)
    setError(null)
    try {
      await upload(
        file,
        docType,
        { insuranceExpires: docType === 'insurance' && expiresOn ? expiresOn : null },
        (pct) => setProgress(pct),
      )
      setProgress(100)
      setPhase('success')
      setTimeout(() => onSuccess(), 450)
    } catch (e) {
      const msg = humanUploadError(e instanceof Error ? e.message : 'Upload failed')
      setError(msg)
      setPhase('error')
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: dragging ? 'rgba(37,99,235,0.18)' : 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
        transition: 'background 0.15s',
      }}
      onClick={onClose}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="doc-upload-modal"
      data-dragging={dragging ? '1' : '0'}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r)',
          padding: '24px',
          width: '100%', maxWidth: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          outline: dragging ? '2px solid var(--accent)' : undefined,
        }}
        onClick={e => e.stopPropagation()}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="doc-upload-dialog"
      >
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', margin: '0 0 20px' }}>
          {title}
        </h2>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Document type
          </label>
          <select
            value={docType}
            onChange={e => setDocType(e.target.value)}
            data-testid="doc-upload-type"
            style={{
              width: '100%', padding: '8px 10px', fontSize: 13,
              border: '1px solid var(--line)', borderRadius: 'var(--rs)',
              background: 'var(--surface)', color: 'var(--ink)',
            }}
          >
            {HUB_DOC_TYPES.map(dt => (
              <option key={dt.value} value={dt.value}>{dt.label}</option>
            ))}
          </select>
        </div>

        {showInsuranceExpiry && docType === 'insurance' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Expires on (optional)
            </label>
            <input
              type="date"
              value={expiresOn}
              onChange={e => setExpiresOn(e.target.value)}
              data-testid="doc-upload-expires"
              style={{
                width: '100%', padding: '8px 10px', fontSize: 13,
                border: '1px solid var(--line)', borderRadius: 'var(--rs)',
                background: 'var(--surface)', color: 'var(--ink)',
              }}
            />
          </div>
        )}

        <div
          onClick={() => inputRef.current?.click()}
          data-testid="doc-upload-dropzone"
          style={{
            border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--line2)'}`,
            borderRadius: 'var(--rs)',
            padding: '24px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'var(--accent-l)' : 'var(--panel)',
            transition: 'all 0.15s',
            marginBottom: 16,
          }}
        >
          <Upload size={20} color="var(--ink4)" style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
          <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--ink3)', fontWeight: 500 }}>
            {dragging ? 'Drop to attach' : 'Drop file anywhere in this dialog — or click to browse'}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink5)' }}>
            PDF, DOCX, XLSX, JPG, PNG — max 25 MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            data-testid="doc-upload-file"
            onChange={e => { const f = e.target.files?.[0]; if (f) void pickFile(f) }}
          />
        </div>

        {file && (
          <UploadFileChip
            filename={file.name}
            size={file.size}
            mime={file.type}
            phase={phase}
            progress={progress}
            error={error}
          />
        )}

        {dupPrompt && (
          <div
            data-testid="doc-upload-dup-warn"
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              background: 'var(--amber-l, #fffbeb)',
              border: '1px solid var(--amber, #f59e0b)',
              borderRadius: 'var(--rs)',
              fontSize: 12,
              color: 'var(--ink)',
            }}
          >
            <p style={{ margin: '0 0 10px' }}>{dupPrompt}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" type="button" onClick={() => { setDupPrompt(null); setFile(null); setPhase('idle') }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                type="button"
                data-testid="doc-upload-anyway"
                onClick={() => void runUpload()}
              >
                Upload anyway
              </button>
            </div>
          </div>
        )}

        {!dupPrompt && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={onClose} disabled={phase === 'uploading'}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => void runUpload()}
              disabled={!file || phase === 'uploading' || phase === 'success'}
              data-testid="doc-upload-submit"
            >
              {phase === 'uploading' ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
